import React, { useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { func, shape, string } from 'prop-types';
import { useParams } from 'react-router-dom';
import * as R from 'ramda';

// Components
import { MDialog } from 'common';
import DialogExplicitConfirmation from 'common/dialog-explicit-confirmation/DialogExplicitConfirmation';

const SelfAuthorizationProductConfirmationDialog = ({
  activeSelfAuthTab,
  currentSelection,
  dialogOpen,
  dialogSelfAuthorization,
  fetchSelfAuthorizations,
  fetchUniqueSelectors,
  setDialogSelfAuthorization,
  setExtendedLoadingError,
  setForceClose,
  setProductActivationError,
  setSelfAuthorizationDialogOpen,
  toggleExtendedLoading,
  toggleProductActivation,
}) => {
  const { viewBy } = useParams();

  const { activeAuthorization, affiliate, product, location } = dialogSelfAuthorization;
  const { active, extendedLoadingAccepted, productLocationAuthorizationId } =
    activeAuthorization || {};

  const productName = product?.nameShort;
  const locationName = location?.nameShort;

  const { primaryName, secondaryName } =
    viewBy === 'location'
      ? { primaryName: productName, secondaryName: locationName }
      : { primaryName: locationName, secondaryName: productName };

  const handleToggleExtendedLoading = () => {
    toggleExtendedLoading({
      extendedLoadingAccepted,
      locationName,
      productLocationAuthorizationId,
      productName,
    });
  };

  const handleToggleProductActivation = async () => {
    await toggleProductActivation({
      active,
      affiliate,
      listType: activeSelfAuthTab,
      locationId: location.id,
      locationName: location.nameShort,
      productId: product.id,
      productLocationAuthorizationId,
      productName: product.nameShort,
    }).then(() => {
      // these two fetch calls should happen at the same time
      fetchUniqueSelectors({ affiliate }, viewBy, false);
      const queryParams = {
        affiliate,
        [`${viewBy}Id`]: currentSelection.id,
      };
      // selfAuthorizations can be updated locally using updateSelfAuthorizations reducer
      // it was decided to update by refetching data to ensure consistency with DB
      // this also eliminates the need to iterate through base products that are activated when activating a custom product
      fetchSelfAuthorizations(queryParams);
    });
  };

  const getDialogInfo = R.cond([
    [
      R.equals('activateProduct'),
      R.always({
        action: handleToggleProductActivation,
        explicit: false,
        header: `Activate ${primaryName}?`,
        message: `Are you sure you would like to turn on Self Authorization for ${productName} at ${locationName}?`,
      }),
    ],
    [
      R.equals('deactivateProduct'),
      R.always({
        action: handleToggleProductActivation,
        explicit: true,
        header: `Deactivate Self Authorization`,
        message: `If you still want to deactivate this Self Authorization, enter the name below and click Confirm.`,
        warningList: [
          'All dependent authorizations will be deactivated',
          'This action cannot be undone once executed.',
        ],
      }),
    ],
    [
      R.equals('activateExtendedLoading'),
      R.always({
        action: handleToggleExtendedLoading,
        explicit: false,
        header: `Activate Extended Loading for ${primaryName}?`,
        message: `Extended Loading for ${primaryName} will be activated at ${secondaryName}`,
      }),
    ],
    [
      R.equals('deactivateExtendedLoading'),
      R.always({
        action: handleToggleExtendedLoading,
        explicit: true,
        header: `Deactivate Extended Loading`,
        message: `If you still want to deactivate Extended Loading, enter the name below and click Confirm.`,
        warningList: [
          'This action will prevent extended loading for all Supplier Authorizations associated with this Self Authorization.',
        ],
      }),
    ],
    [
      R.equals('batchActivate'),
      R.always({
        action: () => setForceClose(true),
        explicit: false,
        header: `Are you sure you want to close the wizard?`,
        message: `By closing the wizard you will no longer be able to view the progress of each activation however, all requests will continue until complete. Please confirm you want to close the wizard.`,
      }),
    ],
    [
      R.T,
      R.always({
        action: () => {},
        explicit: false,
      }),
    ],
  ]);

  const { action, header, message, explicit, warningList } = getDialogInfo(dialogOpen);

  // control and close the confirmation dialog
  const handleCloseDialog = useCallback(() => {
    if (['deactivateProduct', 'activateProduct'].includes(dialogOpen)) {
      setProductActivationError('');
    }
    if (['deactivateExtendedLoading', 'activateExtendedLoading'].includes(dialogOpen)) {
      setExtendedLoadingError('');
    }
    setSelfAuthorizationDialogOpen(null);
    setDialogSelfAuthorization({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return explicit ? (
    <DialogExplicitConfirmation
      confirmationMessage={message}
      confirmationValue={primaryName}
      dialogOpen={!!dialogOpen}
      handleCloseDialog={handleCloseDialog}
      onConfirm={action}
      title={header}
      warningList={warningList}
    />
  ) : (
    <MDialog
      header={header}
      leftButtonAction={handleCloseDialog}
      leftButtonText="Back"
      onClose={handleCloseDialog}
      open={!!dialogOpen}
      rightButtonAction={action}
      rightButtonText="Confirm"
    >
      <Box>
        <Typography variant="body2">{message}</Typography>
      </Box>
    </MDialog>
  );
};

SelfAuthorizationProductConfirmationDialog.propTypes = {
  activeSelfAuthTab: string,
  currentSelection: shape({}).isRequired,
  dialogOpen: string,
  dialogSelfAuthorization: shape({}).isRequired,
  fetchSelfAuthorizations: func.isRequired,
  fetchUniqueSelectors: func.isRequired,
  setDialogSelfAuthorization: func.isRequired,
  setExtendedLoadingError: func.isRequired,
  setForceClose: func.isRequired,
  setProductActivationError: func.isRequired,
  setSelfAuthorizationDialogOpen: func.isRequired,
  toggleExtendedLoading: func.isRequired,
  toggleProductActivation: func.isRequired,
};

SelfAuthorizationProductConfirmationDialog.defaultProps = {
  activeSelfAuthTab: '',
  dialogOpen: null,
};

export default SelfAuthorizationProductConfirmationDialog;
